import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Month } from '@ynab/core'
import { fetchAccounts, fetchLooseEnds, fetchMe, queryKeys, triggerSync } from './api.ts'
import { Header } from './components/Header.tsx'
import { MobileNav } from './components/MobileNav.tsx'
import { Sidebar } from './components/Sidebar.tsx'
import { UpdatePrompt } from './components/UpdatePrompt.tsx'
import { fmtWhole } from './lib/format.ts'
import { daysOf, useRoute } from './lib/router.ts'
import { Ahead } from './views/Ahead.tsx'
import { Ask } from './views/Ask.tsx'
import { Cushion } from './views/Cushion.tsx'
import { Fill } from './views/Fill.tsx'
import { Loans } from './views/Loans.tsx'
import { LookingBack } from './views/LookingBack.tsx'
import { LooseEnds } from './views/LooseEnds.tsx'

export function App() {
  // Screen, month and horizon live in the URL, so a refresh lands you where you were
  // and a link goes where it says.
  const [route, navigate] = useRoute()
  const { screen } = route
  const month = route.month ?? Month.currentMonth()
  const queryClient = useQueryClient()

  const me = useQuery({ queryKey: queryKeys.me, queryFn: fetchMe })
  const accounts = useQuery({ queryKey: queryKeys.accounts, queryFn: fetchAccounts })
  const looseEnds = useQuery({ queryKey: queryKeys.looseEnds, queryFn: fetchLooseEnds })

  const sync = useMutation({
    mutationFn: () => triggerSync(),
    // Everything on every screen is read from the mirror, so a sync stales all of it.
    onSettled: () => queryClient.invalidateQueries(),
  })

  const loose = looseEnds.data
  const looseCount = loose ? loose.connectionErrors.length + loose.unconfirmed.length : 0
  const ready = me.data?.readyToAssign ?? 0
  // One source for both navs, so a badge cannot appear in one and not the other.
  const badges = {
    ...(ready > 0 ? { fill: fmtWhole(ready) } : {}),
    ...(looseCount > 0 ? { loose: String(looseCount) } : {}),
  }

  return (
    <div className="flex min-h-screen bg-paper text-ink">
      <Sidebar
        screen={screen}
        onNavigate={(next) => navigate({ screen: next })}
        accounts={accounts.data ?? []}
        members={me.data?.members ?? []}
        badges={badges}
      />

      <UpdatePrompt />

      <MobileNav
        screen={screen}
        onNavigate={(next) => navigate({ screen: next })}
        badges={badges}
      />

      {/*
        `pb-24` below `lg` clears the fixed tab bar. Without it the last row sits under
        the bar and cannot be scrolled into view, because a fixed element is out of flow
        and contributes no height to scroll to.
      */}
      <main key={screen} className="kete-enter flex min-w-0 flex-1 flex-col pb-24 lg:pb-0">
        <Header
          me={me.data}
          onSync={() => sync.mutate()}
          syncing={sync.isPending}
          syncError={sync.error instanceof Error ? sync.error.message : null}
          month={
            screen === 'back'
              ? {
                  value: month,
                  first: me.data?.budget?.firstMonth,
                  onPrev: () => navigate({ month: Month.previous(month) }),
                  onNext: () => navigate({ month: Month.next(month) }),
                }
              : null
          }
        />

        {screen === 'ahead' ? (
          <Ahead days={daysOf(route)} onDays={(days) => navigate({ days })} />
        ) : screen === 'fill' ? (
          <Fill />
        ) : screen === 'ask' ? (
          <Ask />
        ) : screen === 'loans' ? (
          <Loans />
        ) : screen === 'cushion' ? (
          <Cushion />
        ) : screen === 'loose' ? (
          <LooseEnds />
        ) : (
          <LookingBack month={month} />
        )}
      </main>
    </div>
  )
}
